/**
 * ============================================
 * HYBRID THEME - MAIN JAVASCRIPT
 * Dark Moody Premium
 * Dawn Clone + Full Customization
 * ============================================
 */

(function () {
    'use strict';

    // ========== UTILITY FUNCTIONS ==========
    class ThemeUtils {
        static debounce(fn, wait) {
            let t;
            return (...args) => {
                clearTimeout(t);
                t = setTimeout(() => fn.apply(this, args), wait);
            };
        }

        static throttle(fn, delay) {
            let last = 0;
            return (...args) => {
                const now = Date.now();
                if (now - last >= delay) {
                    last = now;
                    fn.apply(this, args);
                }
            };
        }

        static fetchConfig(type = 'json') {
            return { method: 'GET', headers: { 'Accept': `application/${type}` } };
        }

        static getUrlParameter(name) {
            name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
            const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
            const results = regex.exec(location.search);
            return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
        }

        static formatMoney(cents, format) {
            if (typeof cents === 'string') cents = cents.replace('.', '');
            let value = '';
            const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
            const formatString = format || '${{amount}}';

            function formatWithDelimiters(number, precision, thousands, decimal) {
                precision = precision || 2;
                thousands = thousands || ',';
                decimal = decimal || '.';

                if (isNaN(number) || number == null) return 0;

                number = (number / 100).toFixed(precision);

                const parts = number.split('.');
                const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
                const centsAmount = parts[1] ? decimal + parts[1] : '';

                return dollarsAmount + centsAmount;
            }

            const match = formatString.match(placeholderRegex);
            if (!match) return formatWithDelimiters(cents, 2) + ' ' + formatString;

            switch (match[1]) {
                case 'amount':
                    value = formatWithDelimiters(cents, 2);
                    break;
                case 'amount_no_decimals':
                    value = formatWithDelimiters(cents, 0);
                    break;
                case 'amount_with_comma_separator':
                    value = formatWithDelimiters(cents, 2, '.', ',');
                    break;
                case 'amount_no_decimals_with_comma_separator':
                    value = formatWithDelimiters(cents, 0, '.', ',');
                    break;
                default:
                    value = formatWithDelimiters(cents, 2);
            }

            return formatString.replace(placeholderRegex, value);
        }
    }

    // ========== CART MANAGEMENT ==========
    class CartManager {
        constructor() {
            this.cart = null;
            this.init();
        }

        async init() {
            try {
                const response = await fetch('/cart.js');
                this.cart = await response.json();
                this.dispatchCartUpdated();
            } catch (error) {
                console.error('Error loading cart:', error);
            }
        }

        async addItem(variantId, quantity = 1, properties = {}) {
            try {
                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id: variantId,
                        quantity,
                        properties
                    })
                });

                if (response.ok) {
                    const cartResponse = await fetch('/cart.js');
                    this.cart = await cartResponse.json();
                    this.dispatchCartUpdated();
                    this.showToast('Item added to cart!');
                    return this.cart;
                }
                throw new Error('Failed to add item');
            } catch (error) {
                console.error('Error adding to cart:', error);
                this.showToast('Error adding item to cart', 'error');
            }
        }

        async updateItem(id, quantity) {
            try {
                const response = await fetch('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id, quantity })
                });

                if (response.ok) {
                    this.cart = await response.json();
                    this.dispatchCartUpdated();
                    return this.cart;
                }
                throw new Error('Failed to update item');
            } catch (error) {
                console.error('Error updating cart:', error);
            }
        }

        async removeItem(id) {
            return this.updateItem(id, 0);
        }


        dispatchCartUpdated() {
            document.dispatchEvent(new CustomEvent('cart:updated', {
                detail: {
                    cart: this.cart,
                    count: this.cart ? this.cart.item_count : 0,
                    total: this.cart ? this.cart.total_price : 0
                }
            }));
        }

        showToast(message, type = 'success') {
            document.dispatchEvent(new CustomEvent('toast:show', {
                detail: { message, type }
            }));
        }
    }

    // ========== QUICK VIEW ==========
    class QuickView extends HTMLElement {
        constructor() {
            super();
            this.content = this.querySelector('[data-quick-view-content]');
        }

        connectedCallback() {
            this.initCloseButtons();
            this.initQuickViewTriggers();

            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') {
                    this.close();
                }
            });
        }

        initCloseButtons() {
            this.querySelectorAll('[data-quick-view-close]').forEach(button => {
                button.addEventListener('click', () => this.close());
            });

            this.querySelector('.quick-view-modal__overlay')?.addEventListener('click', () => this.close());
        }

        initQuickViewTriggers() {
            document.addEventListener('click', (e) => {
                const trigger = e.target.closest('[data-quick-view]');
                if (trigger) {
                    e.preventDefault();
                    const productHandle = trigger.dataset.quickView;
                    this.open(productHandle);
                }
            });
        }

        async open(productHandle) {
            this.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';

            try {
                const response = await fetch(`/products/${productHandle}?view=quick-view`);
                const html = await response.text();
                this.content.innerHTML = html;
            } catch (error) {
                console.error('Error loading quick view:', error);
                this.content.innerHTML = '<p>Error loading product. Please try again.</p>';
            }
        }

        close() {
            this.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
            this.content.innerHTML = '<div class="quick-view-modal__loading"><div class="quick-view-modal__spinner"></div></div>';
        }
    }

    customElements.define('quick-view-modal', QuickView);

    // ========== TOAST NOTIFICATIONS ==========
    class ToastNotification extends HTMLElement {
        constructor() {
            super();
            this.message = this.querySelector('[data-toast-message]');
            this.closeBtn = this.querySelector('[data-toast-close]');
        }

        connectedCallback() {
            this.closeBtn?.addEventListener('click', () => this.hide());

            document.addEventListener('toast:show', (e) => {
                this.show(e.detail.message, e.detail.type);
            });
        }

        show(message, type = 'success') {
            if (this.message) {
                this.message.textContent = message;
            }

            this.setAttribute('aria-hidden', 'false');
            this.classList.add('toast--visible');

            clearTimeout(this._timeout);
            this._timeout = setTimeout(() => this.hide(), 3000);
        }

        hide() {
            this.setAttribute('aria-hidden', 'true');
            this.classList.remove('toast--visible');
        }
    }

    customElements.define('toast-notification', ToastNotification);

    // ========== BACK TO TOP ==========
    class BackToTop extends HTMLElement {
        connectedCallback() {
            const button = this.querySelector('.back-to-top__button');

            window.addEventListener('scroll', ThemeUtils.throttle(() => {
                if (window.scrollY > 500) {
                    this.setAttribute('aria-hidden', 'false');
                } else {
                    this.setAttribute('aria-hidden', 'true');
                }
            }, 200));

            button?.addEventListener('click', () => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        }
    }

    customElements.define('back-to-top', BackToTop);

    // ========== GLOBAL EVENT HANDLERS ==========
    document.addEventListener('DOMContentLoaded', () => {

        // Initialize cart manager
        window.cartManager = new CartManager();

        // Add to cart from product cards
        document.addEventListener('click', async (e) => {
            const addToCartBtn = e.target.closest('[data-add-to-cart]');
            if (addToCartBtn && !addToCartBtn.closest('form')) {
                e.preventDefault();
                const variantId = addToCartBtn.dataset.addToCart;
                if (variantId && window.cartManager) {
                    await window.cartManager.addItem(variantId);
                }
            }

            // Remove from cart
            const removeFromCartBtn = e.target.closest('[data-cart-remove]');
            if (removeFromCartBtn) {
                e.preventDefault();
                const itemKey = removeFromCartBtn.dataset.cartRemove;
                if (itemKey && window.cartManager) {
                    removeFromCartBtn.closest('.cart-drawer__item').style.opacity = '0.5';
                    removeFromCartBtn.disabled = true;
                    await window.cartManager.removeItem(itemKey);
                }
            }

            // Wishlist toggle
            const wishlistBtn = e.target.closest('[data-wishlist]');
            if (wishlistBtn) {
                e.preventDefault();
                const productHandle = wishlistBtn.dataset.wishlist;
                wishlistBtn.classList.toggle('is-active');

                // Simple localStorage wishlist
                let wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
                if (wishlist.includes(productHandle)) {
                    wishlist = wishlist.filter(h => h !== productHandle);
                } else {
                    wishlist.push(productHandle);
                }
                localStorage.setItem('wishlist', JSON.stringify(wishlist));
            }
        });

        // Initialize cart toggle from header
        document.addEventListener('click', (e) => {
            const cartToggle = e.target.closest('[data-cart-toggle]');
            if (cartToggle) {
                e.preventDefault();
                document.dispatchEvent(new CustomEvent('cart:toggle'));
            }
        });

        // Listen for cart updates
        document.addEventListener('cart:updated', (e) => {
            const { cart, count, total } = e.detail;
            
            // Update all cart count badges
            document.querySelectorAll('[data-cart-count]').forEach(el => {
                el.textContent = count;
                el.style.display = count > 0 ? '' : 'none';
            });
            
            // Render cart drawer items if available
            const cartDrawerBody = document.querySelector('[data-cart-items]');
            if (cartDrawerBody && cart) {
                if (count === 0) {
                    cartDrawerBody.innerHTML = `
                        <div class="cart-drawer__empty">
                            <div class="cart-drawer__empty-icon">🛒</div>
                            <h3 class="cart-drawer__empty-title">Your cart is empty</h3>
                            <p class="cart-drawer__empty-text">Your cart is currently empty.</p>
                            <a href="/collections/all" class="button button--primary">Continue Shopping</a>
                        </div>
                    `;
                    const footer = document.querySelector('.cart-drawer__footer');
                    if (footer) footer.style.display = 'none';
                } else {
                    let itemsHtml = '<div class="cart-drawer__items">';
                    cart.items.forEach(item => {
                        itemsHtml += `
                            <div class="cart-drawer__item" data-cart-item-key="${item.key}">
                                <div class="cart-drawer__item-image">
                                    <img src="${item.image}" alt="${item.product_title}" width="80" height="100" style="object-fit: cover;">
                                </div>
                                <div class="cart-drawer__item-details">
                                    <h4 class="cart-drawer__item-title">${item.product_title}</h4>
                                    <div class="cart-drawer__item-price">${ThemeUtils.formatMoney(item.final_price)}</div>
                                    <div class="cart-drawer__item-quantity">Qty: ${item.quantity}</div>
                                </div>
                                <button type="button" class="cart-drawer__item-remove" data-cart-remove="${item.key}" aria-label="Remove item">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                    </svg>
                                </button>
                            </div>
                        `;
                    });
                    itemsHtml += '</div>';
                    cartDrawerBody.innerHTML = itemsHtml;
                    
                    const footer = document.querySelector('.cart-drawer__footer');
                    if (footer) footer.style.display = 'block';
                    
                    const totalEl = document.querySelector('.cart-drawer__subtotal-value');
                    if (totalEl) totalEl.textContent = ThemeUtils.formatMoney(total);
                }
            }
        });

        // Initialize search toggle
        document.addEventListener('click', (e) => {
            const searchToggle = e.target.closest('[data-search-toggle]');
            if (searchToggle) {
                e.preventDefault();
                const searchDrawer = document.querySelector('search-drawer');
                if (searchDrawer) {
                    const isOpen = searchDrawer.getAttribute('aria-hidden') === 'false';
                    searchDrawer.setAttribute('aria-hidden', isOpen);
                }
            }
        });

        // Initialize mobile menu
        document.addEventListener('click', (e) => {
            const menuToggle = e.target.closest('[data-menu-toggle]');
            if (menuToggle) {
                e.preventDefault();
                const mobileMenu = document.querySelector('mobile-menu');
                const overlay = document.querySelector('[data-header-overlay]');

                if (mobileMenu) {
                    const isOpen = mobileMenu.getAttribute('aria-hidden') === 'false';
                    mobileMenu.setAttribute('aria-hidden', isOpen);
                    menuToggle.setAttribute('aria-expanded', !isOpen);

                    if (overlay) {
                        overlay.setAttribute('aria-hidden', isOpen);
                    }

                    document.body.style.overflow = isOpen ? '' : 'hidden';
                }
            }
        });

        // Close mobile menu on overlay click
        document.addEventListener('click', (e) => {
            const overlay = e.target.closest('[data-header-overlay]');
            if (overlay && overlay.getAttribute('aria-hidden') === 'false') {
                const mobileMenu = document.querySelector('mobile-menu');
                const menuToggle = document.querySelector('[data-menu-toggle]');

                if (mobileMenu) mobileMenu.setAttribute('aria-hidden', 'true');
                if (menuToggle) menuToggle.setAttribute('aria-expanded', 'false');
                overlay.setAttribute('aria-hidden', 'true');
                document.body.style.overflow = '';
            }
        });

        // Initialize wishlist count
        const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
        document.querySelectorAll('[data-wishlist-count]').forEach(el => {
            el.textContent = wishlist.length;
            el.setAttribute('data-count', wishlist.length);
        });
    });

    // ========== SERVICE WORKER REGISTRATION ==========
    if ('serviceWorker' in navigator) {
        window.addEventListener('load', () => {
            navigator.serviceWorker.register('/sw.js').catch(() => {
                // Service worker registration failed
            });
        });
    }
})();